import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, User } from 'lucide-react';

/**
 * Pick a party by name, from the names actually on the screen.
 *
 * A cashier is handed a name, not a device number — so this is the fastest path
 * through the Outstanding screen and it is worth a panel of its own rather than
 * a dropdown. Three across, big targets, because it is used at a counter with
 * somebody waiting.
 *
 * **The names come from the rows, not from the customer list.** Only parties
 * who currently owe something can be picked, so choosing one can never produce
 * an empty screen — and a venue with two thousand registered customers does not
 * get two thousand chips to scroll.
 */
export function VisitorNamePicker({
  names,
  selected,
  onPick,
  onClose,
}: {
  names: readonly string[];
  selected: string | null;
  onPick: (name: string | null) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const shown = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (!needle) return names;
    return names.filter((name) => name.toLocaleLowerCase().includes(needle));
  }, [names, query]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('outstanding.visitorPicker.title')}
      data-visitor-picker
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 pt-24"
      // Clicking the backdrop closes. A cashier who opened this by accident
      // should not have to find a specific X while somebody waits.
      onClick={onClose}
    >
      {/* `short:max-h-none`: on a phone on its side 70vh is ~260px, less than
          the search box plus the list's own 52vh, and `overflow-hidden` cut
          the last names off where no scroll could reach them. Uncapped, the
          panel is its content's height and the backdrop scrolls. */}
      <div
        onClick={(event) => event.stopPropagation()}
        className="max-h-[70vh] w-full max-w-2xl overflow-hidden rounded-card border border-subtle bg-raised shadow-2xl short:max-h-none"
      >
        <div className="border-b border-subtle p-3">
          <div className="flex items-center gap-2 rounded-full border border-subtle bg-ground px-4 py-2.5">
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('outstanding.visitorPicker.search')}
              aria-label={t('outstanding.visitorPicker.search')}
              className="min-w-0 flex-1 bg-transparent text-sm text-content outline-none placeholder:text-content-muted"
            />
            <Search className="h-4 w-4 shrink-0 text-content-muted" aria-hidden />
          </div>
        </div>

        <div className="max-h-[52vh] overflow-y-auto p-3">
          {/* "Everyone" first and always present: without a way back, picking a
              name is a one-way door and the cashier has to reload the screen. */}
          <button
            type="button"
            data-visitor-any
            onClick={() => {
              onPick(null);
              onClose();
            }}
            className={`mb-2 inline-flex w-full items-center gap-2 rounded-full border px-4 py-2.5 text-sm transition-colors ${
              selected === null
                ? 'border-[var(--brand-primary)] text-content'
                : 'border-subtle text-content-secondary hover:bg-hover hover:text-content'
            }`}
          >
            {t('outstanding.visitorPicker.everyone')}
          </button>

          {shown.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-content-muted">
              {t('outstanding.visitorPicker.none')}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
              {shown.map((name) => (
                <button
                  key={name}
                  type="button"
                  data-visitor-chip={name}
                  onClick={() => {
                    onPick(name);
                    onClose();
                  }}
                  className={`inline-flex min-h-12 items-center gap-2 rounded-full border px-4 py-2 text-start text-sm transition-colors ${
                    selected === name
                      ? 'border-[var(--brand-primary)] bg-hover text-content'
                      : 'border-subtle bg-ground text-content hover:bg-hover'
                  }`}
                >
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-raised">
                    <User className="h-4 w-4 text-content-secondary" aria-hidden />
                  </span>
                  {/* Wrapped rather than truncated: two parties called "mcha
                      jaff" and "mcha jaff & andazyar karo" must not both read
                      "mcha jaff…" on the screen used to decide who pays. */}
                  <span className="min-w-0 break-words">{name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
